import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { noop, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { takeUntil, filter, map } from 'rxjs/operators';
import { AssignmentCustomerResponse, AssignmentPspResponse, ContractResponse } from '../../../../../api';
import { BaseButtonSize } from '../../../../shared/components/button/base-button.component';
import { BaseCoreState, baseAssignmentsSelectors, baseAssignmentsActions } from '../../../../_store';
import { BaseFormatDatePipe } from '../../../../shared/pipes/base-format-date.pipe';
import { BaseModalService } from '../../../../shared/services/base-modal.service';
import { BaseRolesService } from '../../../../core/services/base-roles.service';
import { BaseFileDownloadService } from '../../../../shared/services/base-file-download.service';
import { BASE_PERMISSIONS } from '../../../../core/constants/base-permissions';

@Component({
  selector: 'base-contract-viewer-toolbar',
  templateUrl: './base-contract-viewer-toolbar.component.html',
})
export class BaseContractViewerToolbarComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('regenerateContractModal', { static: true }) regenerateContractModal: ElementRef;
  @Input() src: Blob | undefined;
  @Input() fileName: string | undefined = '';
  @Input() modalRef: NgbModalRef | undefined;
  @Input() isContractValid: boolean;
  @Output() isContractValidChange = new EventEmitter<boolean>();
  @Output() downloadImage = new EventEmitter<any>();

  assignment: AssignmentCustomerResponse | AssignmentPspResponse;
  contractsList: ContractResponse[] = [];
  selectedContract: ContractResponse | undefined;
  createdByMessage: string;
  sanitizedSrc: SafeResourceUrl | null = null;
  isShowRegenerateButton = false;
  validContract: ContractResponse;
  buttonSizes = BaseButtonSize;
  hasSignContractPermission = false;
  regenerateContractModalRef: NgbModalRef | undefined;

  private destroy$ = new Subject<boolean>();

  constructor(
    private store: Store<BaseCoreState>,
    private formatDatePipe: BaseFormatDatePipe,
    private translateService: TranslateService,
    public modalService: BaseModalService,
    private sanitizer: DomSanitizer,
    private roleService: BaseRolesService,
    private fileDownloadService: BaseFileDownloadService
  ) {}

  ngOnInit() {
    this.hasSignContractPermission = this.roleService.hasFeaturePermission(BASE_PERMISSIONS.FEATURE_SIGN_CONTRACT);

    this.store
      .select(baseAssignmentsSelectors.getAssignment)
      .pipe(
        takeUntil(this.destroy$),
        filter((assignmentState) => assignmentState.ok === true),
        map((assignmentState) => assignmentState.data)
      )
      .subscribe((assignment) => {
        const isFirstAssignmentInit = !this.assignment;

        this.assignment = assignment;
        this.contractsList = assignment.contracts as ContractResponse[];
        this.validContract = this.contractsList.find((contract) => contract.valid) as ContractResponse;
        this.updateSelectedContractData(this.validContract);

        if (!isFirstAssignmentInit) {
          this.getContract(this.validContract.id);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.src) {
      this.sanitizedSrc = this.getSanitizedSrc(changes?.src.currentValue);
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getSanitizedSrc(src: Blob): SafeResourceUrl {
    const blobUrl = `${URL.createObjectURL(src)}#toolbar=0`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
  }

  updateSelectedContractInfo(contract: ContractResponse) {
    this.updateSelectedContractData(contract);
    this.getContract(contract.id);
  }

  updateSelectedContractData(contract: ContractResponse) {
    this.selectedContract = contract;
    this.isShowRegenerateButton = this.selectedContract.valid && this.selectedContract.signingStatus !== 'signed';
    this.isContractValid = contract?.valid ?? false;
    this.isContractValidChange.emit(this.isContractValid);
    this.createdByMessage = this.getActiveContractMessage(contract);
  }

  handleContractRegeneration() {
    this.modalRef?.close();

    this.regenerateContractModalRef = this.modalService.open(
      {
        headText: 'contractRegeneration.title',
        bodyTemplate: this.regenerateContractModal,
        hideFooter: true,
      },
      {
        size: 'lg',
        backdrop: 'static',
      }
    );

    this.regenerateContractModalRef.result.then(noop, noop);
  }

  contractListChange(value: ContractResponse) {
    this.updateSelectedContractInfo(value);
  }

  getActiveContractMessage(value: ContractResponse): string {
    const contractCreator = value.regeneratedBy || value.uploadedBy;
    return this.translateService.instant(
      'contractRegeneration.createdByMessage',
      !!contractCreator
        ? {
            creator: `${contractCreator.firstName} ${contractCreator.lastName}`,
            date: this.formatDatePipe.transform(value.createdAt),
          }
        : {
            creator: this.translateService.instant('contractRegeneration.system'),
            date: this.formatDatePipe.transform(value.createdAt),
          }
    );
  }

  getContract(contractId: number) {
    this.store.dispatch(
      baseAssignmentsActions.viewContract({
        payload: { assignment: this.assignment.id, contract: contractId },
      })
    );
  }

  downloadContract(): void {
    if (!this.selectedContract?.id || !this.src) {
      return;
    }

    this.createDownloadLink({
      contract: this.src,
      fileName: this.fileName || '',
    });
  }

  createDownloadLink(data: { contract: Blob; fileName: string }) {
    this.fileDownloadService.triggerBrowserDownload(data.contract, data.fileName);
  }
}
